import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const DialogContext = createContext(null);

// Base Modal Component
const Modal = ({ isOpen, onClose, title, children, maxWidth = 'max-w-md' }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className={`w-full ${maxWidth} rounded-xl shadow-2xl`}
        style={{
          background: 'var(--theme-background-secondary)',
          border: '1px solid var(--theme-border)'
        }}
      >
        <div 
          className="px-6 py-4 flex items-center justify-between"
          style={{ borderBottom: '1px solid var(--theme-border)' }}
        >
          <h3 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>{title}</h3>
          <button 
            onClick={onClose} 
            className="p-2 rounded-lg transition-colors"
            style={{ 
              color: 'var(--theme-text-secondary)'
            }}
          >
            <X className="w-5 h-5 hover:opacity-80" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

export const DialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setDialog({
        type: 'confirm',
        title: options.title || 'Confirm',
        message: options.message,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        variant: options.variant || 'default', // 'default' | 'destructive' | 'success'
        resolve
      });
    });
  }, []);

  const prompt = useCallback((options) => {
    return new Promise((resolve) => {
      setDialog({
        type: 'prompt',
        title: options.title || 'Enter Value',
        message: options.message,
        placeholder: options.placeholder || '',
        defaultValue: options.defaultValue || '',
        submitText: options.submitText || 'Submit',
        cancelText: options.cancelText || 'Cancel',
        inputType: options.inputType || 'text',
        resolve
      });
    });
  }, []);

  const alert = useCallback((options) => {
    return new Promise((resolve) => {
      setDialog({
        type: 'alert',
        title: options.title || 'Alert',
        message: options.message,
        okText: options.okText || 'OK',
        resolve
      });
    });
  }, []);

  const closeDialog = useCallback((result) => {
    if (dialog?.resolve) {
      dialog.resolve(result);
    }
    setDialog(null);
  }, [dialog]);

  const variants = {
    destructive: { background: 'var(--theme-error, #dc2626)' },
    default: { background: 'var(--theme-primary)' },
    success: { background: 'var(--theme-success, #22c55e)' }
  };

  const contextValue = useMemo(() => ({ confirm, prompt, alert }), [confirm, prompt, alert]);

  return (
    <DialogContext.Provider value={contextValue}>
      {children}

      {/* Confirm Dialog */}
      {dialog?.type === 'confirm' && (
        <Modal isOpen={true} onClose={() => closeDialog(false)} title={dialog.title}>
          <div className="space-y-6">
            <p style={{ color: 'var(--theme-text-secondary)' }}>{dialog.message}</p>
            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => closeDialog(false)}
                style={{ 
                  borderColor: 'var(--theme-border)', 
                  color: 'var(--theme-text-secondary)' 
                }}
              >
                {dialog.cancelText}
              </Button>
              <Button 
                onClick={() => closeDialog(true)}
                style={variants[dialog.variant]}
              >
                {dialog.confirmText}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Prompt Dialog */}
      {dialog?.type === 'prompt' && (
        <PromptDialogContent 
          dialog={dialog} 
          onClose={closeDialog}
        />
      )}

      {/* Alert Dialog */}
      {dialog?.type === 'alert' && (
        <Modal isOpen={true} onClose={() => closeDialog()} title={dialog.title}>
          <div className="space-y-6">
            <p style={{ color: 'var(--theme-text-secondary)' }}>{dialog.message}</p>
            <div className="flex justify-end">
              <Button 
                onClick={() => closeDialog()}
                style={{ background: 'var(--theme-primary)' }}
              >
                {dialog.okText}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </DialogContext.Provider>
  );
};

// Separate component for prompt to manage its own state
const PromptDialogContent = ({ dialog, onClose }) => {
  const [value, setValue] = useState(dialog.defaultValue || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onClose(value || null);
  };

  return (
    <Modal isOpen={true} onClose={() => onClose(null)} title={dialog.title}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {dialog.message && <p style={{ color: 'var(--theme-text-secondary)' }}>{dialog.message}</p>}
        <Input
          type={dialog.inputType}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={dialog.placeholder}
          style={{ 
            background: 'var(--theme-background-secondary)', 
            borderColor: 'var(--theme-border)', 
            color: 'var(--theme-text)' 
          }}
          autoFocus
        />
        <div className="flex justify-end gap-3">
          <Button 
            type="button"
            variant="outline" 
            onClick={() => onClose(null)}
            style={{ 
              borderColor: 'var(--theme-border)', 
              color: 'var(--theme-text-secondary)' 
            }}
          >
            {dialog.cancelText}
          </Button>
          <Button 
            type="submit"
            style={{ background: 'var(--theme-primary)' }}
          >
            {dialog.submitText}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

export default DialogContext;
